import * as echarts from 'echarts'
import { useEffect, useRef } from 'react'
import { COLORS, useTheme } from '../lib/theme'

export type OptionBuilder = (c: typeof COLORS) => echarts.EChartsOption

/** §8.0 chart base. Option builders read colours through the getter so a theme switch does not remount. */
export function baseOption(c: typeof COLORS): echarts.EChartsOption {
  return {
    backgroundColor: 'transparent',
    textStyle: { color: c.text, fontFamily: 'Inter, ui-sans-serif, system-ui' },
    grid: { left: 6, right: 8, top: 28, bottom: 6, containLabel: true },
    tooltip: {
      trigger: 'axis',
      backgroundColor: c.tooltipBg,
      borderColor: c.axis,
      borderWidth: 1,
      textStyle: { color: c.tooltipText, fontSize: 12 },
    },
  }
}

export function axisStyle(c: typeof COLORS) {
  return {
    axisLine: { lineStyle: { color: c.axis } },
    axisTick: { show: false },
    axisLabel: { color: c.text, fontSize: 11 },
    splitLine: { lineStyle: { color: c.grid, type: 'dashed' as const } },
  }
}

export function Chart({ build, height = 220, onClick }: { build: OptionBuilder; height?: number; onClick?: (params: echarts.ECElementEvent) => void }) {
  const ref = useRef<HTMLDivElement>(null)
  const chart = useRef<echarts.ECharts | null>(null)
  const { theme } = useTheme()
  const builder = useRef(build)
  builder.current = build

  useEffect(() => {
    if (!ref.current) return
    const inst = echarts.init(ref.current, undefined, { renderer: 'canvas' })
    chart.current = inst
    const ro = new ResizeObserver(() => inst.resize())
    ro.observe(ref.current)
    if (onClick) inst.on('click', onClick)
    return () => {
      ro.disconnect()
      inst.dispose()
      chart.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    chart.current?.setOption(builder.current(COLORS), { notMerge: true })
  }, [build, theme])

  return <div ref={ref} style={{ height, width: '100%' }} />
}
